import { Box, Stack, Button, Tooltip, ClickAwayListener, Divider } from '@mui/material';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@/hooks/use-notifications';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import AccessTimeOutlined from '@mui/icons-material/AccessTimeOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import PeopleOutline from '@mui/icons-material/PeopleOutline';
import ConfirmationModal from '@/components/common/confirmation-modal';
import { useFlowSyncTransport } from '@/state/mutations/flows/use-flow-sync-transport';
import { useEventStore } from '@/state/stores/use-event-store';
import { isMuiPortalDismissExemptTarget } from '../is-mui-portal-dismiss-exempt';
import { useFlowStore } from '@/state/stores/use-flow-store';
import computeAllSlotTimings, { calculateFlowDurationFromSlots } from '../calculate';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import useUpdateSessionMutation from '@/state/mutations/sessions/use-update-session';
import type { UpdateSessionDto } from '@/types/dtos/sessions/request/update-session.dto';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { useFlowMode } from '../../use-flow-mode';
import type { ScheduleInlineDetailsTab } from '../../edit-details/schedule-inline-details-tab';
import { useTranslations } from '@/i18n/use-translations';
import { useTheme } from '@mui/material/styles';
import { slotInlineContainedActionSx, slotInlineOutlinedActionSx } from '../slot-inline-action-buttons';
import { shouldOfferSlotRemoval } from '../session-segment.helpers';
import { getSlotListColumnLayout, type SlotListColumnLayout } from '../slot-list-layout';
import {
  SlotListExpandedLeftColumn,
  slotListExpandedColumnPaddingSx,
  slotListExpandedRightColumnSx,
} from '../slot-list-expanded-row-columns';

export type InlineSlotEditFormProps = {
  slot: FlowSlotDto;
  onClose: () => void;
  onSlotDeleted?: () => void;
  onExpandedChange?: (slotId: string | null) => void;
  onFormTypeChange?: (slotId: string, formType: 'timing' | 'new' | null) => void;
  onShowTimingAlert?: () => void;
  /** Narrow schedule: tab buttons for inline details (session / actors / flows) */
  scheduleInlineTabSwitcher?: {
    tab: ScheduleInlineDetailsTab;
    onChange: (tab: ScheduleInlineDetailsTab) => void;
  };
  /** Match main slot row column widths (schedule drag-handle layout). */
  slotListColumnLayout?: SlotListColumnLayout;
  /** Schedule tab while live: slot is in the playing-prefix of timed slots (no structural edits). */
  scheduleLiveLockBlocked?: boolean;
};

export default function InlineSlotEditForm({
  slot,
  onClose,
  onSlotDeleted,
  onExpandedChange,
  onFormTypeChange,
  onShowTimingAlert,
  scheduleInlineTabSwitcher,
  slotListColumnLayout,
  scheduleLiveLockBlocked = false,
}: InlineSlotEditFormProps) {
  const columnLayout = slotListColumnLayout ?? getSlotListColumnLayout(false);
  const { t } = useTranslation();
  const t2 = useTranslations();
  const notification = useNotifications();
  const flow = useFlowStore((s) => s.flow);
  const { emitFlowSync, canEmit } = useFlowSyncTransport();
  const sessions = useEventStore((s) => s.eventSessions);
  const { mutateAsync: updateSessionMutation, isPending: isUpdatingSession } = useUpdateSessionMutation();
  const mode = useFlowMode();
  const theme = useTheme();

  const setSelectedSlot = useFlowStore((s) => s.setSelectedSlot);

  // Delete dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletePending, setDeletePending] = useState(false);
  const [isDeletingSessionNotInLibrary, setIsDeletingSessionNotInLibrary] = useState(false);

  const sessionTitle = slot?.contentType === 'Session' ? slot?.content?.title : slot?.title;
  const offerSlotRemoval = slot ? shouldOfferSlotRemoval(slot, flow) : false;

  const openDeleteDialog = useCallback(() => {
    if (scheduleLiveLockBlocked) return;
    if (slot?.contentType === 'Session' && slot?.content) {
      setIsDeletingSessionNotInLibrary(!shouldOfferSlotRemoval(slot, flow));
    } else {
      setIsDeletingSessionNotInLibrary(false);
    }
    setIsDeleteDialogOpen(true);
  }, [scheduleLiveLockBlocked, slot, flow]);

  const handleDeleteModalCancel = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setIsDeletingSessionNotInLibrary(false);
  }, []);

  const handleDeleteSession = useCallback(async () => {
    if (!flow || !slot) return;

    setDeletePending(true);
    const filteredSlots = flow.slots?.filter((s) => s.id !== slot.id);
    // Use flow.durationPlannedMinutes as primary source (flow is source of truth)
    const durationToUse = flow.durationPlannedMinutes ?? calculateFlowDurationFromSlots(filteredSlots ?? []);
    const computedSlots = computeAllSlotTimings(filteredSlots ?? [], durationToUse);

    // Extract startTime and endTime from slot if present (matching syncFlow pattern)
    // This preserves the fixed end-time (durationPlannedMinutes) when deleting slots
    let startTime: string | undefined;
    let endTime: string | null | undefined;

    if (flow.slot?.startOffsetMinutes !== undefined) {
      startTime = minuteOffsetToTime(flow.slot.startOffsetMinutes);

      // Use flow.durationPlannedMinutes as primary source (flow is source of truth)
      const flowDurationPlannedMinutes = flow.durationPlannedMinutes;
      // Check if durationPlannedMinutes is explicitly set and greater than 0
      if (flowDurationPlannedMinutes !== undefined && flowDurationPlannedMinutes > 0) {
        // Fixed end-time is set
        endTime = minuteOffsetToTime(flow.slot.startOffsetMinutes + flowDurationPlannedMinutes);
      } else {
        // Fixed end-time is being removed (undefined, null, or <= 0) - explicitly set to null to clear it
        endTime = null;
      }
    }

    // Payload to send to server - use original version
    // Follow the same pattern as syncFlow: convert slot to startTime/endTime strings
    const normalizedSlots = computedSlots.map((slot) => ({
      ...slot,
      content: typeof slot.content === 'object' ? slot.content?.id : slot.content,
    }));
    const flowPayload = {
      ...flow,
      slots: normalizedSlots,
      slot: undefined, // Remove slot object - backend expects startTime/endTime strings
      startDate: undefined,
      ...(startTime && { startTime }),
      // Always include endTime when startTime is present, even if null (to clear it)
      ...(startTime !== undefined && { endTime: endTime ?? null }),
      // Keep original version - don't increment here
    };

    // Update session if it's the last usage
    const session = sessions?.find((s) => s.id === slot?.content?.id);
    if (session) {
      const isLastUsageOfContent = flow?.slots?.filter((s) => s.content?.id === session.id).length === 1;
      if (isLastUsageOfContent) {
        // `entity` can be undefined when the API could not populate the flow/event (see formatSessionUsage)
        const updateSessionDto: UpdateSessionDto = {
          id: session.id,
          version: session.version,
          usedInEntities: session.usedInEntities
            ?.filter((e) => e.entity?.id !== flow?.id)
            .flatMap((e) => {
              const entityId = e.entity?.id;
              if (entityId === undefined) {
                return [];
              }
              return [{ entity: entityId, entityType: e.entityType }];
            }),
        };

        try {
          await updateSessionMutation(updateSessionDto);
        } catch {
          notification.notifyError(t('flows.detail.slotList.detail.deleteSlot.notifications.deleteError'));
          setDeletePending(false);
          return;
        }
      }
    }

    if (!canEmit) {
      notification.notifyError(t('flows.detail.slotList.detail.deleteSlot.notifications.deleteError'));
      setDeletePending(false);
      return;
    }

    // Optimistically update the flow store with incremented version
    // Use original flow with updated slots (not normalizedSlots which has string IDs)
    const setFlow = useFlowStore.getState().setFlow;
    const optimisticFlow: FlowDto = {
      ...flow,
      slots: computedSlots,
      version: (flow.version ?? 0) + 1,
      ...(startTime && { startTime }),
      ...(startTime !== undefined && { endTime: endTime ?? null }),
    };
    setFlow(optimisticFlow);

    const timeoutId = setTimeout(() => {
      setFlow(flow);
      notification.notifyError(t('flows.detail.slotList.detail.deleteSlot.notifications.deleteError'));
      setDeletePending(false);
      setIsDeleteDialogOpen(false);
    }, 10000);

    void (async () => {
      const { ok } = await emitFlowSync({ flow: flowPayload as Record<string, unknown> });
      clearTimeout(timeoutId);

      if (ok) {
        notification.notifySuccess(
          isDeletingSessionNotInLibrary
            ? t('flows.detail.slotList.detail.deleteSlot.notifications.deleteSuccess', { title: sessionTitle || '' })
            : t('flows.detail.slotList.detail.deleteSlot.notifications.removeSessionSuccess', {
                title: sessionTitle || '',
              }),
        );
        setIsDeleteDialogOpen(false);
        setSelectedSlot(undefined);
        onExpandedChange?.(null);
        onSlotDeleted?.();
      } else {
        setFlow(flow);
        notification.notifyError(t('flows.detail.slotList.detail.deleteSlot.notifications.deleteError'));
        setDeletePending(false);
        setIsDeleteDialogOpen(false);
      }
    })();
  }, [
    flow,
    slot,
    sessions,
    updateSessionMutation,
    emitFlowSync,
    canEmit,
    isDeletingSessionNotInLibrary,
    sessionTitle,
    setSelectedSlot,
    onExpandedChange,
    onSlotDeleted,
    notification,
    t,
  ]);

  // Handle click away - same pattern as timing form
  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      // On mobile, touch events need more time to propagate and execute
      // The ClickAwayListener fires on mouseDown/touchStart, but we need to wait
      // for the click/tap to complete before closing
      const target = event.target as HTMLElement;
      const isTouchEvent = 'touches' in event || 'changedTouches' in event;

      if (isMuiPortalDismissExemptTarget(event)) {
        return;
      }

      const inlineDetailsRoot = target.closest('[data-slot-inline-details]');
      if (inlineDetailsRoot instanceof HTMLElement && inlineDetailsRoot.getAttribute('data-slot-id') === slot.id) {
        return;
      }

      // Check if clicking on timing section or middle section of ANY slot
      // These sections handle their own clicks and should not trigger close
      const clickedSlotCard = target.closest('[data-testid="flow-slot"]') as HTMLElement | null;
      if (clickedSlotCard) {
        const cardFirstChild = clickedSlotCard.firstElementChild;
        const timingSection = cardFirstChild?.firstElementChild;
        const isClickingTimingSection = timingSection && (timingSection.contains(target) || timingSection === target);
        const isClickingMiddleSection = target.closest('[data-cursor-element-id="cursor-el-195"]');

        // Don't close if clicking on timing or middle sections - let those handlers manage expansion
        if (isClickingTimingSection || isClickingMiddleSection) {
          return;
        }
      }

      // Check if clicking on another slot (to open its form)
      const getClickedSlotId = () => {
        const path = (event as Event & { composedPath?: () => EventTarget[] })?.composedPath?.();
        const candidates: (HTMLElement | EventTarget)[] = [];

        if (path && Array.isArray(path)) candidates.push(...path);
        candidates.push(target);

        // Walk up the DOM as a fallback
        let el: HTMLElement | null = target;
        while (el) {
          candidates.push(el);
          el = el.parentElement;
        }

        // Filter to only HTMLElement nodes that have getAttribute method
        for (const candidate of candidates) {
          if (candidate instanceof HTMLElement) {
            const slotId = candidate.getAttribute('data-slot-id');
            if (slotId) return slotId;
          }
        }
        return null;
      };

      const clickedSlotId = getClickedSlotId();

      // If clicking on another slot, don't close (let that slot's handler manage it)
      if (clickedSlotId && clickedSlotId !== slot.id) {
        return;
      }

      // Delay for touch events to ensure tap completes
      const delay = isTouchEvent ? 100 : 0;

      if (delay > 0) {
        setTimeout(() => {
          onClose();
        }, delay);
      } else {
        onClose();
      }
    },
    [slot.id, onClose],
  );

  return (
    <>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ width: '100%', height: '32px', minHeight: '32px', maxHeight: '32px' }}
        >
          <SlotListExpandedLeftColumn layout={columnLayout}>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              size="small"
              title={t('flows.detail.slotList.detail.tooltips.editSlotTiming')}
              aria-label={t('flows.detail.slotList.detail.tooltips.editSlotTiming')}
              onClick={(e) => {
                e.stopPropagation();
                if (isOrganizeMode(mode)) {
                  onShowTimingAlert?.();
                } else if (slot?.id && onFormTypeChange) {
                  onFormTypeChange(slot.id, 'timing');
                }
              }}
              disabled={isOrganizeMode(mode)}
              sx={{
                width: '100%',
                minWidth: 0,
                ...slotInlineOutlinedActionSx,
              }}
            >
              <AccessTimeOutlined fontSize="small" />
            </Button>
          </SlotListExpandedLeftColumn>
          <Divider orientation="vertical" flexItem />
          {/* Middle section — inline schedule tabs (session / actors / flows) */}
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              ...slotListExpandedColumnPaddingSx,
              flex: 1,
              minWidth: 0,
              gap: 0.5,
              width: '100%',
            }}
          >
            {scheduleInlineTabSwitcher ? (
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                {(
                  [
                    { id: 'session' as const, Icon: DescriptionOutlined, labelKey: 'session' },
                    { id: 'actors' as const, Icon: PeopleOutline, labelKey: 'actors' },
                  ] as const
                ).map(({ id, Icon, labelKey }) => {
                  const label = t2(`flows.detail.editDetails.inlineTab.${labelKey}`, {
                    defaultValue:
                      labelKey === 'session' ? 'Session details' : labelKey === 'actors' ? 'Actors' : 'Flows',
                  });
                  const active = scheduleInlineTabSwitcher.tab === id;
                  return (
                    <Box
                      component="span"
                      key={id}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Tooltip title={label} leaveDelay={0} disableInteractive>
                        <Box component="span" sx={{ display: 'block', width: '100%' }}>
                          <Button
                            type="button"
                            variant={active ? 'contained' : 'outlined'}
                            color="inherit"
                            size="small"
                            fullWidth
                            disableElevation
                            disableRipple
                            aria-label={label}
                            aria-pressed={active}
                            onClick={(e) => {
                              e.stopPropagation();
                              scheduleInlineTabSwitcher.onChange(id);
                            }}
                            sx={{
                              minWidth: 0,
                              px: 0.5,
                              ...(active ? slotInlineContainedActionSx(theme) : slotInlineOutlinedActionSx),
                            }}
                          >
                            <Icon fontSize="small" />
                          </Button>
                        </Box>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Stack>
            ) : null}
          </Stack>
          <Divider orientation="vertical" flexItem />
          {/* Right section — delete/remove session */}
          {slot && (slot.contentType === 'Session' || slot.contentType === 'Break') && (
            <Stack direction="row" sx={slotListExpandedRightColumnSx(columnLayout)}>
              <Tooltip
                title={
                  offerSlotRemoval
                    ? t('flows.detail.slotList.detail.deleteSlot.removeSessionTitle')
                    : t('flows.detail.slotList.detail.deleteSlot.sessionTitle')
                }
                leaveDelay={0}
                disableInteractive
              >
                <Box component="span" sx={{ display: 'block', width: '100%' }}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={openDeleteDialog}
                    disabled={isOrganizeMode(mode) || scheduleLiveLockBlocked || deletePending || isUpdatingSession}
                    sx={{ minWidth: 0, width: '100%', ...slotInlineOutlinedActionSx }}
                  >
                    {offerSlotRemoval ? <RemoveCircleOutline fontSize="small" /> : <DeleteOutlined fontSize="small" />}
                  </Button>
                </Box>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </ClickAwayListener>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={isDeleteDialogOpen}
        isLoading={deletePending}
        title={
          isDeletingSessionNotInLibrary
            ? t('flows.detail.slotList.detail.deleteSlot.sessionTitle')
            : t('flows.detail.slotList.detail.deleteSlot.removeSessionTitle')
        }
        text={
          isDeletingSessionNotInLibrary
            ? t('flows.detail.slotList.detail.deleteSlot.sessionText', { title: sessionTitle || '' })
            : t('flows.detail.slotList.detail.deleteSlot.removeSessionText', { title: sessionTitle || '' })
        }
        onConfirm={handleDeleteSession}
        onCancel={handleDeleteModalCancel}
      />
    </>
  );
}
